/**
 * Servizio Firestore per le sessioni Race Engineer.
 * Collezione: re_sessions — globale per team + pista.
 */
import {
  collection, addDoc, query, where, orderBy,
  limit, getDocs, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

const COLL = 're_sessions';

// ── Salva sessione dopo coaching ─────────────────────────────────
export async function saveRaceSession({
  teamId, trackSlug, trackName, driverName, date,
  lapTime_s, lapTimeStr, refLapTimeStr,
  sectors, coachingType, summary, findings,
  setupA, setupB, weather, vehicleName,
}) {
  return addDoc(collection(db, COLL), {
    teamId,
    trackSlug,
    trackName:      trackName    || trackSlug,
    driverName:     driverName   || 'Pilota',
    date:           date         || new Date().toLocaleDateString('it-IT'),
    lapTime_s:      lapTime_s    || null,
    lapTimeStr:     lapTimeStr   || '',
    refLapTimeStr:  refLapTimeStr || null,
    sectors:        (sectors || []).map(s => ({
      sector:        s.sector,
      duration_s:    s.duration_s,
      delta_vs_ref_s: s.delta_vs_ref_s ?? null,
      delta_vs_best_s: s.delta_vs_best_s ?? null,
    })),
    coachingType:   coachingType || null,
    summary:        summary      || '',
    findings:       (findings    || []).slice(0, 5).map(f => ({
      curva:     f.curva,
      problema:  f.problema  || '',
      consiglio: f.consiglio || '',
      priorita:  f.priorita  ?? 2,
    })),
    setupA:         setupA  || null,
    setupB:         setupB  || null,
    weather:        weather || null,
    vehicleName:    vehicleName || null,
    timestamp:      serverTimestamp(),
  });
}

// ── Carica ultime N sessioni per pista ───────────────────────────
export async function getRecentSessions({ teamId, trackSlug, n = 10 }) {
  const q = query(
    collection(db, COLL),
    where('teamId',    '==', teamId),
    where('trackSlug', '==', trackSlug),
    orderBy('timestamp', 'desc'),
    limit(n),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── Costruisce contesto storico da passare all'AI ────────────────
export function buildHistoryContext(sessions) {
  if (!sessions?.length) return null;

  const withLap = sessions.filter(s => s.lapTime_s);
  const trend = withLap.length >= 2
    ? +(withLap[withLap.length - 1].lapTime_s - withLap[0].lapTime_s).toFixed(3)
    : null;

  // Problemi ricorrenti: conta quante volte appare ogni settore/area
  const problemCount = {};
  sessions.forEach(s =>
    (s.findings || []).forEach(f => {
      const k = f.curva;
      if (!k) return;
      problemCount[k] = (problemCount[k] || 0) + 1;
    })
  );
  const ricorrenti = Object.entries(problemCount)
    .filter(([, n]) => n >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([area, n]) => `${area} (×${n})`);

  // Evoluzione setup
  const setupEvol = sessions
    .filter(s => s.setupA && s.lapTimeStr)
    .slice(0, 4)
    .map(s => ({ data: s.date, lapTime: s.lapTimeStr, setup: s.setupA }))
    .reverse();

  // Ultimi coaching
  const ultimaGuida  = sessions.find(s => s.coachingType === 'guida');
  const ultimoSetup  = sessions.find(s => s.coachingType === 'setup');

  return {
    sessioni_precedenti: sessions.length,
    trend_tempo_giro: trend != null
      ? (trend <= 0 ? `miglioramento ${Math.abs(trend)}s` : `peggioramento ${trend}s`)
      : 'primo giro su questa pista',
    tempi_storici: withLap.slice(0, 5).map(s => ({ data: s.date, tempo: s.lapTimeStr })),
    problemi_ricorrenti: ricorrenti,
    evoluzione_setup: setupEvol,
    ultimo_coaching_guida:  ultimaGuida?.summary  || null,
    ultimo_coaching_setup:  ultimoSetup?.summary  || null,
  };
}

// ── Converte lapTimeStr "1:23.456" → secondi ─────────────────────
export function lapTimeToSeconds(str) {
  if (!str) return null;
  try {
    const [min, rest] = str.split(':');
    return parseFloat(min) * 60 + parseFloat(rest);
  } catch { return null; }
}
